'use client';

import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';
import { asLink } from '@prismicio/helpers';
import classNames from 'classnames';
import { NavigationDocumentDataLinksItem } from 'prismicio-types';

import { Icon, Link, MegaMenuCustom, useData } from '@/components';
import { isSingleMegaMenu } from '@/components/MegaMenuCustom';
import { filterNavigationLinksByVisibility, NavigationViewport } from '@/utils';

import style from './nav.module.scss';

interface NavProps {
  viewport?: NavigationViewport;
  className?: string;
}

const FOCUSABLE = 'a[href], button:not([disabled]), [role="button"]';

const Nav: FC<NavProps> = ({ viewport = 'desktop', className }) => {
  const { navigation } = useData();
  const pathname = usePathname();
  const navbarRef = useRef<HTMLElement | null>(null);
  const triggerRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const isMobile = viewport === 'mobile';
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const visibleLinks = filterNavigationLinksByVisibility(
    navigation.data?.links,
    viewport,
  );

  useEffect(() => {
    setExpandedIndex(null);
  }, [pathname]);

  // Close on outside focus/pointer (iOS-safe). On mobile, also fix Tab order
  // for the portaled panel (siblings → -1; last link → next nav item).
  useEffect(() => {
    if (expandedIndex === null) return;

    const trigger = triggerRefs.current[expandedIndex];
    const panel = document.getElementById(`megamenu-${expandedIndex}`);
    if (!trigger || !panel) return;

    const isInside = (node: EventTarget | null) =>
      node instanceof Node &&
      (node === trigger || trigger.contains(node) || panel.contains(node));

    const onOutside = (e: Event) => {
      if (!isInside(e.target)) setExpandedIndex(null);
    };

    document.addEventListener('focusin', onOutside);
    document.addEventListener('pointerdown', onOutside, true);
    document.addEventListener('touchstart', onOutside, true);

    let restoreTabOrder: (() => void) | undefined;

    if (isMobile) {
      const list = navbarRef.current?.querySelector<HTMLElement>(
        `.${style.navbar_list}`,
      );

      if (list) {
        const items = [...list.querySelectorAll<HTMLElement>(FOCUSABLE)];
        const nextItem = items[items.indexOf(trigger) + 1] ?? null;
        const siblings = items.filter(el => el !== trigger);
        const prevTabIndex = siblings.map(
          el => [el, el.getAttribute('tabindex')] as const,
        );

        siblings.forEach(el => el.setAttribute('tabindex', '-1'));

        const onPanelTab = (e: KeyboardEvent) => {
          if (e.key !== 'Tab' || e.shiftKey) return;
          const links = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
          if (document.activeElement !== links.at(-1)) return;

          e.preventDefault();
          setExpandedIndex(null);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => nextItem?.focus());
          });
        };

        panel.addEventListener('keydown', onPanelTab);
        restoreTabOrder = () => {
          panel.removeEventListener('keydown', onPanelTab);
          prevTabIndex.forEach(([el, value]) =>
            value === null
              ? el.removeAttribute('tabindex')
              : el.setAttribute('tabindex', value),
          );
        };
      }
    }

    return () => {
      document.removeEventListener('focusin', onOutside);
      document.removeEventListener('pointerdown', onOutside, true);
      document.removeEventListener('touchstart', onOutside, true);
      restoreTabOrder?.();
    };
  }, [expandedIndex, isMobile]);

  const toggleExpandedIndex = (index: number) => {
    setExpandedIndex(prev => (prev === index ? null : index));
  };

  const setNavbarRef = (node: HTMLElement | null) => {
    navbarRef.current = node;
    setPortalNode(node);
  };

  const renderPanel = (index: number, uid: string, isExpanded: boolean) => (
    <MegaMenuCustom
      anchorElement={
        isExpanded && isSingleMegaMenu(navigation, uid)
          ? triggerRefs.current[index]
          : null
      }
      className={classNames(style.navbar_menu, {
        [style.navbar_menu__hidden]: !isExpanded,
      })}
      id={`megamenu-${index}`}
      itemID={uid}
      navbarRef={navbarRef}
      viewport={viewport}
    />
  );

  const navList = (
    <ul className={style.navbar_list} key={pathname}>
      {visibleLinks.map((item: NavigationDocumentDataLinksItem, index) => {
        const { type, uid } = item.external_link;
        const isExpandable = ['custom_mega_menu', 'mega_nav'].includes(type);
        const href = asLink(item?.external_link) || item?.link || '/';
        const isExpanded = expandedIndex === index;

        if (isExpandable) {
          return (
            <li
              className={style.navbar_list__nav_item}
              key={index}
              onMouseEnter={
                isMobile ? undefined : () => setExpandedIndex(index)
              }
              onMouseLeave={
                isMobile
                  ? undefined
                  : (e: MouseEvent<HTMLLIElement>) => {
                      if (e.currentTarget.contains(document.activeElement)) {
                        return;
                      }
                      setExpandedIndex(null);
                    }
              }
            >
              <button
                aria-controls={`megamenu-${index}`}
                aria-expanded={isExpanded}
                aria-haspopup="true"
                aria-label={`Expand ${item?.label} submenu`}
                className={style.navbar_list__item}
                onClick={() => toggleExpandedIndex(index)}
                onKeyDown={e => {
                  if (e.key === 'Escape' && isExpanded) {
                    e.preventDefault();
                    setExpandedIndex(null);
                    e.currentTarget.focus();
                  }
                }}
                ref={el => {
                  triggerRefs.current[index] = el;
                }}
                type="button"
              >
                <span className={style.navbar_list__label}>{item?.label}</span>
                <Icon aria-hidden="true" name="angle-up" />
              </button>
              {!!uid &&
                (isMobile
                  ? portalNode &&
                    createPortal(
                      renderPanel(index, uid, isExpanded),
                      portalNode,
                    )
                  : renderPanel(index, uid, isExpanded))}
            </li>
          );
        }

        return (
          <li className={style.navbar_list__nav_item} key={index}>
            <Link
              aria-label={`Go to ${item?.label}`}
              className={style.navbar_list__item}
              href={href}
            >
              {item?.label}
            </Link>
          </li>
        );
      })}
    </ul>
  );

  return (
    <nav
      aria-label="Main navigation"
      className={classNames(style.navbar, className, {
        [style.navbar_mobile]: isMobile,
      })}
      ref={setNavbarRef}
    >
      {isMobile ? (
        <div className={style.navbar_mobile__scroll_wrap}>
          <div className={style.navbar_mobile__scroll}>{navList}</div>
          <div
            aria-hidden="true"
            className={style.navbar_mobile__scroll_fade}
          />
        </div>
      ) : (
        navList
      )}
    </nav>
  );
};

export default Nav;
